import { format, isToday, isYesterday, isSameDay, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { TeamChatMessage } from '../../services/teamChatService';

export const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatMessageTime = (value?: string | null): string => {
  const d = parseDate(value);
  return d ? format(d, 'HH:mm') : '';
};

/** Rótulo curto para a lista de conversas: hora hoje, "Ontem", ou data. */
export const formatConversationTimestamp = (value?: string | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Ontem';
  return format(d, 'dd/MM/yy');
};

export const formatDayDivider = (value?: string | null): string => {
  const d = parseDate(value);
  if (!d) return '';
  if (isToday(d)) return 'Hoje';
  if (isYesterday(d)) return 'Ontem';
  return format(d, "EEEE, d 'de' MMMM", { locale: ptBR });
};

export const needsDayDivider = (current: TeamChatMessage, previous?: TeamChatMessage): boolean => {
  const c = parseDate(current.created_at);
  const p = previous ? parseDate(previous.created_at) : null;
  if (!c) return false;
  if (!p) return true;
  return !isSameDay(c, p);
};

/**
 * Mensagens seguidas do mesmo autor em até 3 minutos são agrupadas: escondem
 * avatar e nome, deixando o fluxo mais limpo (padrão Slack/WhatsApp).
 */
export const isGroupedWithPrevious = (
  current: TeamChatMessage,
  previous?: TeamChatMessage,
): boolean => {
  if (!previous) return false;
  if (current.message_type === 'system' || previous.message_type === 'system') return false;
  if (current.sender_id !== previous.sender_id) return false;
  const c = parseDate(current.created_at);
  const p = parseDate(previous.created_at);
  if (!c || !p) return false;
  if (!isSameDay(c, p)) return false;
  return differenceInMinutes(c, p) < 3;
};

export const initialsOf = (name?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const firstName = (name?: string | null): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return parts[0] || 'Colega';
};

export const formatFileSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Resolve URL relativa de anexo (`/uploads/...`) contra o backend. */
export const resolveAttachmentUrl = (url?: string | null, apiBase?: string): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (apiBase || '').replace(/\/api\/v\d+\/?$/, '').replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

export const messagePreviewText = (message?: TeamChatMessage | null): string => {
  if (!message) return '';
  if (message.is_deleted) return 'Mensagem apagada';
  switch (message.message_type) {
    case 'image':
      return '📷 Imagem';
    case 'audio':
      return '🎤 Áudio';
    case 'file':
      return `📎 ${message.attachment_name || 'Arquivo'}`;
    default:
      return message.body || '';
  }
};

/**
 * A aba pode estar "visible" com o navegador atrás de outro programa; só conta
 * como lida a mensagem que chega com a janela realmente em foco.
 */
export const isUserViewingPage = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();

export const SYSTEM_ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  admin_master: 'Admin master',
  professional: 'Profissional',
  receptionist: 'Recepção',
  financial: 'Financeiro',
  assistant: 'Assistente',
};

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];
